/* import __COLOCATED_TEMPLATE__ from './list-item.hbs'; */
/* RESPONSIBLE TEAM: team-conversational-knowledge */

import Component from '@glimmer/component';
import { ReviewType } from 'embercom/models/content-service/content-review-request';
import type ContentWrapper from 'embercom/models/content-service/content-wrapper';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import { EntityType } from 'embercom/models/data/entity-types';
import {
  humanReadableObjectNames,
  objectIcons,
  objectNames,
} from 'embercom/models/data/matching-system/matching-constants';
import type KnowledgeHubEditorService from 'embercom/services/knowledge-hub-editor-service';
import type ContentSuggestionsService from 'embercom/services/content-suggestions-service';
import { EditorMode } from 'embercom/components/knowledge-hub/content-editor/drawer-editor';
import { tracked } from '@glimmer/tracking';
import type AiContentLibraryApi from 'embercom/lib/ai-content-library/list-api';

interface Args {
  contentWrapper: ContentWrapper;
  activeContentId?: number;
  activeContentType?: string;
  listApi?: AiContentLibraryApi;
  borderOnHover?: boolean;
  showTopics?: boolean;
  showTitle?: boolean;
  loadSuggestions?: () => Promise<void>;
}

export default class ListItem extends Component<Args> {
  @service declare intl: IntlService;
  @service declare knowledgeHubEditorService: KnowledgeHubEditorService;
  @service declare contentSuggestionsService: ContentSuggestionsService;
  @service declare appService: $TSFixMe;

  @tracked isHovering = false;

  get content() {
    return this.args.contentWrapper.contents.firstObject;
  }

  get projectedConversationsCount() {
    return this.args.contentWrapper.contents.firstObject?.contentData.projected_conversations_count;
  }

  get projectedConversationsRanking() {
    return this.args.contentWrapper.contents.firstObject?.contentData
      .projected_conversations_ranking;
  }

  get isActive() {
    if (!this.args.activeContentId || !this.args.activeContentType) {
      return false;
    }
    return (
      Number(this.args.activeContentId) === this.contentId &&
      this.args.activeContentType === this.contentObjectName
    );
  }

  get contentId() {
    return this.args.contentWrapper.entityId!;
  }

  get contentType() {
    return this.content?.contentType;
  }

  get contentObjectName() {
    return objectNames[this.contentType!];
  }

  get numberOfPendingSuggestions() {
    return this.content?.contentData.number_of_pending_review_requests;
  }

  get reviewReason() {
    if (this.content?.contentReviewType === ReviewType.UNDERPERFORMING_CONTENT) {
      return this.intl.t('ai-agent.suggestions.list-item.underperforming-content-reason', {
        contentTitle: this.args.contentWrapper.title,
      });
    }
    if (this.args.contentWrapper.contentReviewSuggestionMetadata.seed_content_explanation) {
      return this.args.contentWrapper.contentReviewSuggestionMetadata.seed_content_explanation;
    }
    if (this.content?.contentData.latest_content_review_request_reason) {
      return this.content?.contentData.latest_content_review_request_reason;
    }
    if (this.content?.contentReviewType === ReviewType.DUPLICATE_CONTENT) {
      return this.intl.t(
        'knowledge-hub.content-editor.side-by-side-review.duplicate-review.reason-generic',
      );
    }
    return this.intl.t('ai-agent.suggestions.list-item.review-reason-fallback');
  }

  get topic(): string {
    let suggestionMetadata = this.args.contentWrapper.contentReviewSuggestionMetadata;
    if (suggestionMetadata?.topic) {
      return suggestionMetadata?.topic;
    }
    return '';
  }

  get subtopics(): string[] {
    let suggestionMetadata = this.args.contentWrapper.contentReviewSuggestionMetadata;
    if (suggestionMetadata?.subtopics) {
      return suggestionMetadata.subtopics;
    }
    return [];
  }

  get mostRecentContentReviewRequestCreatedAt() {
    return this.content?.contentData.most_recent_content_review_request_created_at;
  }

  get leastRecentContentReviewRequestCreatedAt() {
    return this.content?.contentData.least_recent_content_review_request_created_at;
  }

  get suggestedAt() {
    return this.args.listApi?.searchParams?.sort_direction === 'asc'
      ? this.leastRecentContentReviewRequestCreatedAt
      : this.mostRecentContentReviewRequestCreatedAt;
  }

  get icon() {
    if (!this.contentType) {
      return 'article';
    }
    return objectIcons[this.contentType];
  }

  get title() {
    let reviewType = this.content?.contentReviewType;
    let contentTitle = this.args.contentWrapper.title;
    let objectKey = this.contentType === EntityType.ContentSnippet ? 'snippet' : 'article';

    let contentType: string;

    switch (reviewType) {
      case ReviewType.CREATE_CONTENT:
        contentType = 'ai-agent.suggestions.list-item.titles.add-content';
        break;
      case ReviewType.EDIT_CONTENT:
        contentType = `ai-agent.suggestions.list-item.titles.edit-${objectKey}`;
        break;
      case ReviewType.DELETE_CONTENT:
        contentType = `ai-agent.suggestions.list-item.titles.delete-${objectKey}`;
        break;
      case ReviewType.DUPLICATE_CONTENT:
        contentType = 'ai-agent.suggestions.list-item.titles.duplicate-content';
        break;
      case ReviewType.CONTRADICTING_CONTENT:
        contentType = 'ai-agent.suggestions.list-item.titles.contradicting-content';
        break;
      case ReviewType.UNDERPERFORMING_CONTENT:
        contentType = `ai-agent.suggestions.list-item.titles.underperforming-${objectKey}`;
        break;
      default:
        contentType = `ai-agent.suggestions.list-item.titles.fallback-${objectKey}`;
    }

    let title = this.intl.t(contentType);
    return this.args.showTitle ? `${title}: ${contentTitle}` : title;
  }

  get conversationsCount() {
    let conversations;
    if (this.content?.contentReviewType === ReviewType.UNDERPERFORMING_CONTENT) {
      conversations =
        this.args.contentWrapper.contentReviewSuggestionMetadata.unresolved_conversation_ids;
    } else {
      conversations =
        this.args.contentWrapper.contentReviewSuggestionMetadata.included_conversations;
    }
    return conversations ? new Set(conversations).size : 0;
  }

  get editorMode(): EditorMode.REVIEW {
    return EditorMode.REVIEW;
  }

  get isCompleted() {
    return this.contentSuggestionsService.isSuggestionCompleted({
      entityId: this.args.contentWrapper.entityId!,
      entityType: this.args.contentWrapper.entityType,
    });
  }

  get conversationsCountTooltipText() {
    if (this.content?.contentReviewType === ReviewType.UNDERPERFORMING_CONTENT) {
      return this.intl.t('ai-agent.suggestions.list-item.tooltip.unresolved-conversations', {
        contentTypeName: humanReadableObjectNames[this.contentType!].toLowerCase(),
      });
    }
    return this.intl.t('ai-agent.suggestions.list-item.impact-description', {
      conversation_count: this.conversationsCount,
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Suggestions::ListItem': typeof ListItem;
    'ai-agent/suggestions/list-item': typeof ListItem;
  }
}
